// client/src/pages/pmPortal/OwnerMaintenancePage.js
// Read-only, deliberately. Where a job needs the owner's approval the portal says so
// and names the figure, but the approval itself is recorded by the managing broker —
// an approval of record taken in an external session would need its own audit and
// re-authentication, which is a later phase, not a checkbox.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, Pill, money, fmtDate, titleCase, statusTone }
    from '../../components/secure/pm/PMKit';

const OwnerMaintenancePage = () => {
    const api = usePmPortalApiClient();
    const [orders, setOrders] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([
            api.get('/api/pm-portal/owner/work-orders'),
            api.get('/api/pm-portal/owner/vendor-invoices'),
        ])
            .then(([o, i]) => { setOrders(o); setInvoices(i); })
            .catch(e => setError(e.message));
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load maintenance" message={error} />;
    if (!orders || !invoices) return <Loading />;

    const open = orders.filter(o => !['complete', 'cancelled'].includes(o.status));
    const awaitingOwner = orders.filter(o => o.owner_approval_required && !o.owner_approved_on);
    const spend = invoices
        .filter(i => i.status !== 'void' && i.status !== 'disputed')
        .reduce((s, i) => s + Number(i.amount || 0), 0);

    return (
        <>
            <PageHeader title="Maintenance" subtitle="Work raised on your properties, and what it has cost." />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Stat label="Open work orders" value={open.length} />
                <Stat
                    label="Waiting on your approval"
                    value={awaitingOwner.length}
                    tone={awaitingOwner.length ? 'warn' : 'default'}
                    sub={awaitingOwner.length ? 'The office will contact you' : null}
                />
                <Stat label="Invoiced to your book" value={money(spend)} />
            </div>

            {awaitingOwner.length > 0 && (
                <Notice
                    tone="warn"
                    title="Some work is above your approved spending limit"
                    message="These jobs will not be sent to a contractor until you approve the figure. The office will call you; approval is recorded by the managing broker, not through this portal."
                />
            )}

            <Panel title="Work orders">
                <Table
                    columns={[
                        { key: 'wo_no', label: 'No.', render: r => r.wo_no || `#${r.work_order_id}` },
                        { key: 'property_name', label: 'Property' },
                        { key: 'unit_number', label: 'Unit' },
                        { key: 'title', label: 'Work' },
                        { key: 'priority', label: 'Priority',
                          render: r => <Pill tone={r.is_emergency ? 'bad' : r.priority === 'urgent' ? 'warn' : 'slate'}>{titleCase(r.priority)}</Pill> },
                        { key: 'status', label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        { key: 'reported_on', label: 'Reported', render: r => fmtDate(r.reported_on) },
                        { key: 'estimate_amount', label: 'Estimate', align: 'right', render: r => money(r.estimate_amount) },
                        { key: 'actual_amount', label: 'Actual', align: 'right', render: r => money(r.actual_amount) },
                    ]}
                    rows={orders}
                    rowTone={r => (r.owner_approval_required && !r.owner_approved_on ? 'warn' : null)}
                    empty="No work has been raised on your properties."
                />
            </Panel>

            <Panel title="Vendor invoices" subtitle="Charged to your book when approved, paid when funds are released.">
                <Table
                    columns={[
                        { key: 'invoice_no', label: 'Invoice' },
                        { key: 'vendor_name', label: 'Vendor' },
                        { key: 'property_name', label: 'Property' },
                        { key: 'invoice_date', label: 'Date', render: r => fmtDate(r.invoice_date) },
                        { key: 'amount', label: 'Amount', align: 'right', render: r => money(r.amount) },
                        { key: 'status', label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                    ]}
                    rows={invoices}
                    empty="No invoices have been recorded."
                />
            </Panel>
        </>
    );
};

export default OwnerMaintenancePage;
